import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { OpenInBrowserButton } from "@/components/dashboard/browser/OpenInBrowser";
import {
  Search,
  ChevronDown,
  Users,
  RefreshCw,
  Plus,
  X,
  Trash2,
  Upload,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import CustomerDetail from "@/components/dashboard/CustomerDetail";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const ACTION_ITEMS = [
  { label: "Create sales receipt" },
  { label: "Create estimate" },
  { label: "Create sales order" },
  { label: "Create charge" },
  { label: "Create statement" },
  { label: "Create task" },
  { label: "Make inactive" },
  { label: "Request feedback" },
  { label: "Create contract", dot: "#e0219a" },
  { label: "Schedule appointment", dot: "#2ca01c" },
];

const CustomersTab = ({ onOpenProject }) => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(() => searchParams.get("customer") || null);

  // URL-addressable detail view — refresh/share keeps you on the customer.
  const openCustomer = (id) => {
    setSelectedId(id);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (id) p.set("customer", id); else p.delete("customer");
        return p;
      },
      { replace: true }
    );
  };

  // Balance chips deep-link into Billing → Who-owes-what focused on the customer.
  const openReceivable = (name) => {
    if (!name) return;
    setSearchParams({ tab: "billing", receivable: name });
  };
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const toggleCheck = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (ids, allChecked) => {
    setChecked(allChecked ? new Set() : new Set(ids));
  };

  // App-side only delete — QuickBooks is never touched.
  const deleteSelected = async () => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Delete ${ids.length} customer${ids.length === 1 ? "" : "s"}?`,
      description: "This removes them from the app only. QuickBooks is not affected, and a Sync will bring back any that still exist there.",
      confirmLabel: `Delete ${ids.length}`,
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => api.delete(`/customers/${id}`)));
      toast.success(`${ids.length} customer${ids.length === 1 ? "" : "s"} deleted`);
      setCustomers((prev) => prev.filter((c) => !checked.has(c.customer_id)));
      setChecked(new Set());
    } catch {
      toast.error("Could not delete every customer");
      load();
    } finally {
      setDeleting(false);
    }
  };

  // Pull every customer from QuickBooks (read-only — never writes to QBO).
  const syncFromQbo = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/customers/sync-qbo");
      toast.success(`Synced ${r.data.synced} customers from QuickBooks (${r.data.created} new)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "QuickBooks sync failed — is QuickBooks connected?");
    } finally {
      setSyncing(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/customers");
      // Defensive: never render the same customer twice (legacy duplicate docs
      // in old databases corrupt React keys and break search/click).
      const seen = new Set();
      setCustomers((r.data || []).filter((c) => {
        if (seen.has(c.customer_id)) return false;
        seen.add(c.customer_id);
        return true;
      }));
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/customers/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported, updated = 0, skipped = 0 } = r.data;
      toast.success(`Imported ${imported} new contact${imported === 1 ? "" : "s"}${updated ? `, updated ${updated}` : ""}${skipped ? `, skipped ${skipped} without a name` : ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Use a QuickBooks .xls/.xlsx export or a contacts .json file.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const notReady = (what) => toast.info(`${what} — coming soon`);

  if (selectedId) {
    return (
      <CustomerDetail
        customerId={selectedId}
        customers={customers}
        onBack={() => openCustomer(null)}
        onSelect={(id) => openCustomer(id)}
        onUpdated={load}
        onOpenProject={onOpenProject}
      />
    );
  }

  const filtered = customers.filter((c) => {
    const q = query.toLowerCase();
    return (
      !q ||
      c.name?.toLowerCase().includes(q) ||
      c.company_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="bg-[#f4f5f8] p-4 sm:p-6" data-testid="customers-tab">
      <div className="mx-auto mb-6 max-w-6xl">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Database</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Customers</h1>
      </div>
      <div className="mx-auto max-w-6xl rounded-lg bg-white shadow-sm">
        {/* toolbar */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              data-testid="customer-search"
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-[#2ca01c] focus:outline-none focus:ring-1 focus:ring-[#2ca01c]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={syncFromQbo}
              disabled={syncing}
              data-testid="sync-qbo-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-emerald-600 hover:border-emerald-500/60 disabled:opacity-50 dark:text-emerald-300"
              title="Pull all customers from QuickBooks (read-only — nothing is sent to QuickBooks)"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync QBO"}
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              data-testid="import-customers-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#1a3fd1] hover:bg-[#1a3fd1]/25 disabled:opacity-50 dark:text-[#8fa4f0]"
              title="Import contacts from a .json export or a QuickBooks .xls/.xlsx/.csv"
            >
              {importing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {importing ? "Importing…" : "Import"}
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              data-testid="add-customer-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#1a3fd1]/85"
              title="Add a customer in the app only — it is not sent to QuickBooks"
            >
              <Plus className="h-4 w-4" /> Add customer
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xls,.xlsx,.csv,.json,application/json"
              onChange={handleImport}
              className="hidden"
              data-testid="import-file-input"
            />
          </div>
        </div>

        {/* selection bar */}
        {checked.size > 0 && (
          <div className="flex items-center justify-between border-b border-red-200 bg-red-50 px-4 py-2" data-testid="customer-select-bar">
            <span className="font-mono text-[10px] uppercase tracking-widest text-red-600">{checked.size} selected</span>
            <button
              onClick={deleteSelected}
              disabled={deleting}
              data-testid="customer-delete-selected"
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-[#ffffff] hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Deleting…" : "Delete selected"}
            </button>
          </div>
        )}

        {/* table header */}
        <div className="hidden grid-cols-[36px_2fr_2fr_1.2fr_1fr_1.4fr] items-center gap-3 border-b border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 md:grid">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[#2ca01c]"
            data-testid="customer-select-all"
            checked={filtered.length > 0 && filtered.every((c) => checked.has(c.customer_id))}
            onChange={() => toggleAll(filtered.map((c) => c.customer_id), filtered.every((c) => checked.has(c.customer_id)))}
          />
          <span>Name</span>
          <span>Company name</span>
          <span>Phone</span>
          <span>Open balance</span>
          <span className="text-right">Action</span>
        </div>

        {/* rows */}
        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading customers…</div>
        ) : (
          <div className="max-h-[62vh] overflow-y-auto">
            {filtered.map((c) => (
              <div
                key={c.customer_id}
                data-testid="customer-row"
                className="group flex items-center gap-2 border-b border-gray-100 px-3 py-2 hover:bg-[#f0f7ff] md:grid md:grid-cols-[36px_2fr_2fr_1.2fr_1fr_1.4fr] md:gap-3 md:px-4 md:py-3"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#2ca01c]"
                  data-testid={`customer-check-${c.customer_id}`}
                  checked={checked.has(c.customer_id)}
                  onChange={() => toggleCheck(c.customer_id)}
                  onClick={(e) => e.stopPropagation()}
                />
                {/* Mobile: compact single-line card (name + company + balance) */}
                <button
                  type="button"
                  onClick={() => openCustomer(c.customer_id)}
                  data-testid="customer-name-link"
                  className="flex min-w-0 items-center gap-2 text-left md:contents"
                >
                  <Users className="h-3.5 w-3.5 shrink-0 text-[#2c6eeb] md:hidden" />
                  <span className="flex min-w-0 flex-1 items-baseline justify-between gap-2 md:contents">
                    <span className="flex min-w-0 flex-col md:contents">
                      <span className="truncate text-sm font-semibold text-[#2c6eeb] hover:underline">{c.name}</span>
                      <span className="truncate font-mono text-[10px] uppercase tracking-widest text-gray-400 md:hidden">
                        {(c.company_name || c.phone || "—").trim()}
                      </span>
                    </span>
                    {Number(c.open_balance || 0) > 0 ? (
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`customer-owes-mobile-${c.customer_id}`}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); openReceivable(c.name); }}
                        title={`See what ${c.name} owes`}
                        className={`shrink-0 font-mono text-xs font-semibold underline decoration-dotted underline-offset-2 md:hidden ${Number(c.overdue_balance) > 0 ? "text-red-600" : "text-amber-700"}`}
                      >
                        {money(c.open_balance)}
                      </span>
                    ) : (
                      <span className="shrink-0 font-mono text-xs font-semibold text-gray-800 md:hidden">
                        {money(c.open_balance)}
                      </span>
                    )}
                  </span>
                </button>
                <span className="hidden text-sm text-gray-600 md:block">{c.company_name || ""}</span>
                <span className="hidden text-sm text-gray-600 md:block">{c.phone || c.cell_phone || ""}</span>
                <span className="hidden md:block">
                  {Number(c.open_balance || 0) > 0 ? (
                    <button
                      type="button"
                      onClick={() => openReceivable(c.name)}
                      data-testid={`customer-owes-${c.customer_id}`}
                      title={`See what ${c.name} owes in QuickBooks`}
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[11px] font-semibold transition-colors ${
                        Number(c.overdue_balance) > 0
                          ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                          : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      }`}
                    >
                      {money(c.open_balance)}
                    </button>
                  ) : (
                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-[11px] text-gray-500">
                      $0.00
                    </span>
                  )}
                </span>
                <div className="hidden items-center justify-end md:flex">
                  <div className="flex items-center rounded-md">
                    <OpenInBrowserButton url={c.website} name={c.name} testid={`customer-website-${c.customer_id}`} className="mr-1" />
                    <button
                      onClick={() => notReady("Create invoice")}
                      className="text-sm font-medium text-[#2c6eeb] hover:underline"
                    >
                      Create invoice
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid="customer-action-dropdown"
                          aria-label="More actions"
                          className="ml-1 rounded p-1 text-[#2c6eeb] hover:bg-blue-50"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52 bg-white text-gray-700">
                        {ACTION_ITEMS.map((item) => (
                          <DropdownMenuItem
                            key={item.label}
                            onClick={() => notReady(item.label)}
                            className="flex items-center justify-between text-sm"
                          >
                            {item.label}
                            {item.dot && (
                              <span className="ml-2 h-2 w-2 rounded-full" style={{ background: item.dot }} />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="py-16 text-center text-sm text-gray-400">
                No customers found. Import a QuickBooks file to get started.
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 text-xs text-gray-400">
          <span>{filtered.length} customers</span>
        </div>
      </div>
      {addOpen && (
        <AddCustomerDialog
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); load(); }}
        />
      )}
    </div>
  );
};

// ---------------- Add customer (app-side only — never sent to QuickBooks) ----------------
const ADD_FIELDS = [
  ["name", "Name", true],
  ["company_name", "Company", false],
  ["email", "Email", false],
  ["phone", "Phone", false],
  ["cell_phone", "Cell phone", false],
  ["website", "Website", false],
  ["street_address", "Street address", false],
  ["city", "City", false],
  ["province", "Province", false],
  ["zip", "Postal code", false],
  ["notes", "Notes", false],
];

const AddCustomerDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success(`Added ${form.name} (app only — not sent to QuickBooks)`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" onClick={onClose} data-testid="add-customer-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2ca01c]">App only — not sent to QuickBooks</p>
            <h3 className="text-lg font-bold uppercase tracking-tight text-gray-800">Add customer</h3>
          </div>
          <button onClick={onClose} data-testid="add-customer-close" className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-500 hover:text-gray-800">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto p-4">
          {ADD_FIELDS.map(([key, label, required]) => (
            <div key={key}>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gray-500">
                {label}{required ? " *" : ""}
              </label>
              <input
                value={form[key] || ""}
                onChange={(e) => setForm((s) => ({ ...s, [key]: e.target.value }))}
                data-testid={`add-customer-${key}`}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-[#2ca01c] focus:outline-none"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="add-customer-save" className="rounded-md bg-[#2ca01c] px-4 py-1.5 text-xs font-semibold text-[#ffffff] hover:bg-[#248015] disabled:opacity-40">
            {saving ? "Saving…" : "Add customer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomersTab;
